import { PromotionalContentPlacement } from '@/enums/promotional-content-placement';
import { usePromotionalContent } from '@/hooks/promotional-content/promotional-content';
import { PromoContentRelationshipType } from '@cinemark/content-api-client';
import { PromotionalImage } from './content-types/promotional-image';
import { PromotionalImageWithText } from './content-types/promotional-image-with-text';
import { PromotionalImageWithTextAndVideo } from './content-types/promotional-image-with-text-and-video';
import { PromotionalText } from './content-types/promotional-text/promotional-text';
import { PromotionalVideo } from './content-types/promotional-video/promotional-video';

type BasePromotionalContentProps = {
  dataLinkCategory: string;
  dataLinkName?: string;
  imageClassName?: string;
  numberOfItemsToShow?: number;
  placement: PromotionalContentPlacement;
};

type TheatersPromotionalContentProps = BasePromotionalContentProps & {
  promoContentRelationshipType: PromoContentRelationshipType.Theaters;
  relationId?: never;
};

type OtherPromotionalContentProps = BasePromotionalContentProps & {
  promoContentRelationshipType: Exclude<PromoContentRelationshipType, PromoContentRelationshipType.Theaters | PromoContentRelationshipType.Unknown>;
  relationId: string;
};

type PromotionalContentProps = TheatersPromotionalContentProps | OtherPromotionalContentProps;

export const PromotionalContent = ({
  dataLinkCategory,
  dataLinkName,
  imageClassName,
  numberOfItemsToShow,
  placement,
  promoContentRelationshipType,
  relationId,
}: PromotionalContentProps): React.JSX.Element | null => {
  const { data: promoContents, isLoading } = usePromotionalContent({
    pageSize: numberOfItemsToShow,
    payloadId: relationId,
    placement,
    relationType: promoContentRelationshipType,
  });

  if (isLoading || !promoContents?.length) {
    return null;
  }

  return <>
    {promoContents.map((promoContent, index) => {
      if (promoContent.promoContentType === 'promoImage') {
        return (
          <PromotionalImage key={promoContent.id || index}
            className={imageClassName}
            dataLinkCategory={dataLinkCategory}
            dataLinkName={dataLinkName}
            promoContent={promoContent}
          />
        );
      }

      if (promoContent.promoContentType === 'promoImageWithText') {
        return (
          <PromotionalImageWithText key={promoContent.id || index}
            dataLinkCategory={dataLinkCategory}
            dataLinkName={dataLinkName}
            promoContent={promoContent}
          />
        );
      }

      if (promoContent.promoContentType === 'promoImageWithTextAndVideo') {
        return (
          <PromotionalImageWithTextAndVideo key={promoContent.id || index}
            dataLinkCategory={dataLinkCategory}
            dataLinkName={dataLinkName}
            promoContent={promoContent}
          />
        );
      }

      if (promoContent.promoContentType === 'promoVideo') {
        return (
          <PromotionalVideo key={promoContent.id || index}
            dataLinkCategory={dataLinkCategory}
            dataLinkName={dataLinkName}
            promoContent={promoContent}
          />
        );
      }

      return (
        <PromotionalText key={promoContent.id || index}
          dataLinkCategory={dataLinkCategory}
          dataLinkName={dataLinkName}
          promoContent={promoContent}
        />
      );
    })}
  </>;
};
